// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import { IPermissions } from "@/types/keycloak";
import { normalizeProblems } from "@/components/monitoring/lib/problems";
import { interpretSaveError } from "./api";
import { MONITORING_EDITOR_GET_CONFIG, MONITORING_EDITOR_LIST_CONFIG, MONITORING_EDITOR_LIST_PRESETS, MONITORING_EDITOR_RENDER_WIDGET, MONITORING_EDITOR_RESET_TO_PRESET, MONITORING_EDITOR_SAVE_CONFIG, MONITORING_EDITOR_VALIDATE_CONFIG, } from "./queries";
import { EMonitoringSaveStatus, EMonitoringValidationResult, } from "./types";
var asRender = function (value) {
    var response = (value !== null && value !== void 0 ? value : {});
    return __assign(__assign({}, response), { diagnostics: normalizeProblems(response.diagnostics) });
};
/** Harvest's monitoring actions, as the editor's components need them; all run as `monitoring-configure`. */
export var useMonitoringEditorApi = function (electionEventId) {
    var client = useApolloClient();
    return useMemo(function () {
        var context = { headers: { "x-hasura-role": IPermissions.MONITORING_CONFIGURE } };
        var first = function (data) {
            var value = Object.values(data !== null && data !== void 0 ? data : {})[0];
            if (value === undefined || value === null) {
                throw new Error("The action returned no data");
            }
            return value;
        };
        var mutate = function (mutation, variables) { return __awaiter(void 0, void 0, void 0, function () {
            var data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, client.mutate({
                            mutation: mutation,
                            variables: __assign({ election_event_id: electionEventId }, variables),
                            context: context,
                        })];
                    case 1:
                        data = (_a.sent()).data;
                        return [2 /*return*/, first(data)];
                }
            });
        }); };
        var query = function (document, variables) { return __awaiter(void 0, void 0, void 0, function () {
            var data;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, client.query({
                            query: document,
                            variables: __assign({ election_event_id: electionEventId }, variables),
                            fetchPolicy: "network-only",
                            context: context,
                        })];
                    case 1:
                        data = (_a.sent()).data;
                        return [2 /*return*/, first(data)];
                }
            });
        }); };
        var api = {
            validateConfig: function (_a) {
                return __awaiter(this, arguments, void 0, function (_b) {
                    var reply;
                    var kind = _b.kind, key = _b.key, yaml = _b.yaml;
                    return __generator(this, function (_c) {
                        switch (_c.label) {
                            case 0: return [4 /*yield*/, query(MONITORING_EDITOR_VALIDATE_CONFIG, { kind: kind, key: key, yaml: yaml })];
                            case 1:
                                reply = _c.sent();
                                return [2 /*return*/, {
                                        result: reply.result === EMonitoringValidationResult.VALID
                                            ? EMonitoringValidationResult.VALID
                                            : EMonitoringValidationResult.INVALID,
                                        problems: normalizeProblems(reply.problems),
                                        preview: reply.preview ? asRender(reply.preview) : null,
                                    }];
                        }
                    });
                });
            },
            saveConfig: function (_a) {
                return __awaiter(this, arguments, void 0, function (_b) {
                    var reply, error_1, outcome;
                    var kind = _b.kind, key = _b.key, yaml = _b.yaml, expected_revision = _b.expected_revision, change = _b.change;
                    return __generator(this, function (_c) {
                        switch (_c.label) {
                            case 0:
                                _c.trys.push([0, 2, , 3]);
                                return [4 /*yield*/, mutate(MONITORING_EDITOR_SAVE_CONFIG, {
                                        kind: kind,
                                        key: key,
                                        yaml: yaml !== null && yaml !== void 0 ? yaml : null,
                                        expected_revision: expected_revision !== null && expected_revision !== void 0 ? expected_revision : null,
                                        change: change,
                                    })];
                            case 1:
                                reply = _c.sent();
                                return [2 /*return*/, {
                                        status: EMonitoringSaveStatus.SAVED,
                                        revision: reply.revision,
                                        generation: reply.generation,
                                        warnings: normalizeProblems(reply.warnings),
                                        author: reply.author,
                                        created_at: reply.created_at,
                                    }];
                            case 2:
                                error_1 = _c.sent();
                                outcome = interpretSaveError(error_1);
                                if (outcome)
                                    return [2 /*return*/, outcome];
                                throw error_1;
                            case 3: return [2 /*return*/];
                        }
                    });
                });
            },
            renderWidget: function (request) {
                return __awaiter(this, void 0, void 0, function () {
                    var _a;
                    var _b, _c;
                    return __generator(this, function (_d) {
                        switch (_d.label) {
                            case 0:
                                _a = asRender;
                                return [4 /*yield*/, query(MONITORING_EDITOR_RENDER_WIDGET, __assign(__assign({}, request), { election_id: (_b = request.election_id) !== null && _b !== void 0 ? _b : null, draft: (_c = request.draft) !== null && _c !== void 0 ? _c : null }))];
                            case 1: return [2 /*return*/, _a.apply(void 0, [_d.sent()])];
                        }
                    });
                });
            },
            getConfig: function (_a) {
                return __awaiter(this, arguments, void 0, function (_b) {
                    var reply;
                    var _c;
                    var kind = _b.kind, key = _b.key, revision = _b.revision;
                    return __generator(this, function (_d) {
                        switch (_d.label) {
                            case 0: return [4 /*yield*/, query(MONITORING_EDITOR_GET_CONFIG, {
                                    kind: kind,
                                    key: key,
                                    revision: revision !== null && revision !== void 0 ? revision : null,
                                })];
                            case 1:
                                reply = _d.sent();
                                return [2 /*return*/, {
                                        kind: kind,
                                        key: key,
                                        yaml: (_c = reply.yaml) !== null && _c !== void 0 ? _c : null,
                                        revision: reply.revision,
                                        origin: reply.origin,
                                        author: reply.author,
                                        created_at: reply.created_at,
                                    }];
                        }
                    });
                });
            },
            listConfig: function () {
                return __awaiter(this, void 0, void 0, function () {
                    var reply;
                    var _a;
                    return __generator(this, function (_b) {
                        switch (_b.label) {
                            case 0: return [4 /*yield*/, query(MONITORING_EDITOR_LIST_CONFIG, {})];
                            case 1:
                                reply = _b.sent();
                                return [2 /*return*/, (_a = reply.documents) !== null && _a !== void 0 ? _a : []];
                        }
                    });
                });
            },
            listPresets: function () {
                return __awaiter(this, void 0, void 0, function () {
                    var _a, reply, current;
                    var _b;
                    return __generator(this, function (_c) {
                        switch (_c.label) {
                            case 0: return [4 /*yield*/, Promise.all([
                                    query(MONITORING_EDITOR_LIST_PRESETS, {}),
                                    // Which one the event uses only picks the first choice.
                                    query(MONITORING_EDITOR_LIST_CONFIG, {}).then(function (config) { var _a; return (_a = config.preset) === null || _a === void 0 ? void 0 : _a.id; }, function () { return undefined; }),
                                ])];
                            case 1:
                                _a = _c.sent(), reply = _a[0], current = _a[1];
                                return [2 /*return*/, ((_b = reply.presets) !== null && _b !== void 0 ? _b : []).map(function (preset) { return (__assign(__assign({}, preset), { current: preset.id === current })); })];
                        }
                    });
                });
            },
            resetToPreset: function (presetId) {
                return __awaiter(this, void 0, void 0, function () {
                    var reply;
                    return __generator(this, function (_a) {
                        switch (_a.label) {
                            case 0: return [4 /*yield*/, mutate(MONITORING_EDITOR_RESET_TO_PRESET, { preset_id: presetId })];
                            case 1:
                                reply = _a.sent();
                                return [2 /*return*/, { generation: reply.generation }];
                        }
                    });
                });
            },
        };
        return api;
    }, [client, electionEventId]);
};
